import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { cashflowApi } from '../services/cashflowApi';
import { Vehicle, won } from '../types/vehicle';
import { Deal, Valuation } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow } from '../types/cashflow';
import { nowLocal } from '../lib/date';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { CLOSING_TASK, CLOSING_TASKS, ClosingTask, ClosingTaskCode, closingApi } from '../services/vicsApi';

/**
 * 조합 결산 — 사업연도가 끝나면 결산서를 만들어 낸다.
 *
 * 벤처투자조합은 사업연도 종료 후 3개월 안에 결산서를 낸다(회계감사가 걸리기도 한다).
 * 결산서는 회계사가 쓴다. 이 화면은 **회계사에게 넘길 자료가 다 있는지**와 **어디까지 왔는지**를 본다.
 *
 * 할 일 6개의 끝낸 때는 `investment_closing_tasks` 에 남는다(자금원 × 사업연도 × 할 일).
 *
 * ⚠️ 절차 항목은 일반적인 순서다. 규약·라이선스 확인 뒤 실제 의무에 맞춘다.
 */
const TASKS = CLOSING_TASKS;

export const ClosingPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [vals, setVals] = useState<Valuation[]>([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(2025);
  const [tasks, setTasks] = useState<ClosingTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tourRun, setTourRun] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d, c, f, va] = await Promise.all([vehicleApi.list(), dealApi.list(), companyApi.list(), cashflowApi.list(), dealApi.valuations()]);
      setVehicles(v); setDeals(d); setCompanies(c); setFlows(f); setVals(va);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { closingApi.list(year).then(setTasks).catch((e) => setError(e.message)); }, [year]);

  const targets = vehicles.filter((v) => v.legalForm === '조합');
  // 사업연도 — 조합 등록(없으면 운용 개시) 해부터 올해까지(2026-11-02 — 2025 · 2026 만 고를 수 있어 첫 해를 못 봤다)
  const years = (() => {
    const now = new Date().getFullYear();
    const starts = targets.map((v) => Number((v.registeredOn ?? v.startedOn ?? `${now}`).slice(0, 4)));
    const from = starts.length ? Math.min(...starts) : now - 1;
    return Array.from({ length: now - from + 1 }, (_, i) => now - i);
  })();
  const today = new Date().toISOString().slice(0, 10);
  const cName = (id: number) => companies.find((c) => c.id === id)?.name ?? '—';
  const row = (vid: number, t: ClosingTaskCode) => tasks.find((x) => x.vehicleId === vid && x.task === t);
  const toggle = async (vid: number, t: ClosingTaskCode) => {
    setError(null);
    try {
      const r = await closingApi.toggle(row(vid, t), vid, year, t);
      setTasks((xs) => [...xs.filter((x) => x.id !== r.id), r]);
    } catch (e) { setError((e as Error).message); }
  };

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'year', target: '[data-tour="year"]', title: '사업연도', content: '결산할 해를 고릅니다. 기한은 사업연도 종료 후 3개월입니다.' },
    { name: 'tasks', target: '[data-tour="tasks"]', title: '진행', content: '원장 마감부터 제출까지. 누르면 완료 표시가 바뀌고, 끝낸 날이 남습니다.' },
    { name: 'pack', target: '[data-tour="pack"]', title: '회계사에게 넘길 자료', content: '그 해 원장 합계와 연말 평가 목록입니다. 연말 평가가 빠진 투자 건이 여기 뜹니다.' },
  ], []);

  if (loading) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">결산 <TourButton onClick={() => setTourRun(true)} /></h1>
        <select data-tour="year" aria-label="사업연도" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white">
          {years.map((y) => <option key={y} value={y}>{y} 사업연도</option>)}
        </select>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">{error}</div>}

      {targets.map((v) => {
        const yFlows = flows.filter((f) => f.vehicleId === v.id && f.occurredOn.startsWith(String(year)));
        const inSum = yFlows.filter((f) => f.direction === 'in').reduce((s, f) => s + f.amount, 0);
        const outSum = yFlows.filter((f) => f.direction === 'out').reduce((s, f) => s + f.amount, 0);
        const held = deals.filter((d) => d.vehicleId === v.id && d.investedOn && d.investedOn <= `${year}-12-31`);
        const yearEnd = held.map((d) => {
          const val = vals.filter((x) => x.dealId === d.id && x.valuedOn <= `${year}-12-31`)
            .sort((a, b) => b.valuedOn.localeCompare(a.valuedOn))[0];
          const fresh = val && val.valuedOn >= `${year}-10-01`;
          return { d, val, fresh };
        });
        const doneN = TASKS.filter((t) => row(v.id, t)?.doneAt).length;
        return (
          <section key={v.id} className="mb-6">
            <div className="flex items-baseline justify-between bg-white border border-slate-200 rounded-lg px-4 py-3 mb-3">
              <Link to={`/vehicles/${v.id}`} className="font-semibold text-slate-900 hover:text-indigo-600">{v.name}</Link>
              {(() => {
                // 기한이 지났는데 다 끝내지 않았으면 빨갛게 — 결산보고(VICS 1340) 미제출은 과태료 사유(060 문서)
                const due = `${year + 1}-03-31`;
                const over = doneN < TASKS.length && today > due;
                const days = Math.floor((new Date(today).getTime() - new Date(due).getTime()) / 86_400_000);
                return (
                  <span className={`text-xs ${over ? 'text-red-600 font-medium' : doneN === TASKS.length ? 'text-emerald-700' : 'text-slate-500'}`}>
                    기한 {year + 1}.03.31{over ? ` · ${days}일 지남` : ''} · {doneN}/{TASKS.length}{doneN === TASKS.length ? ' 완료' : ''}
                  </span>
                );
              })()}
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <div data-tour="tasks" className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-50">
                {TASKS.map((t, i) => {
                  const ok = !!row(v.id, t)?.doneAt;
                  return (
                    <button key={t} onClick={() => toggle(v.id, t)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50">
                      {ok ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-slate-300" />}
                      <span className="text-xs text-slate-400 w-4 tabular-nums">{i + 1}</span>
                      <span className={`text-sm ${ok ? 'text-slate-500 line-through' : 'text-slate-900'}`}>{CLOSING_TASK.toUi(t)}</span>
                      {ok && <span className="ml-auto text-xs text-slate-400 tabular-nums">{nowLocal(new Date(row(v.id, t)!.doneAt!)).slice(0, 10).replace(/-/g, '.')}</span>}
                    </button>
                  );
                })}
              </div>
              <div data-tour="pack" className="bg-white border border-slate-200 rounded-lg">
                <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">회계사에게 넘길 자료</h2>
                <div className="grid grid-cols-3 gap-2 px-4 py-3 border-b border-slate-100 text-sm">
                  <div><div className="text-xs text-slate-400">건수</div><div className="font-bold tabular-nums">{yFlows.length}</div></div>
                  <div><div className="text-xs text-slate-400">입금 합계</div><div className="font-bold tabular-nums">{won(inSum)}</div></div>
                  <div><div className="text-xs text-slate-400">출금 합계</div><div className="font-bold tabular-nums">{won(outSum)}</div></div>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {yearEnd.map(({ d, val, fresh }) => (
                      <tr key={d.id} className="border-b border-slate-50 last:border-b-0">
                        <td className="px-4 py-2"><Link to={`/deals/${d.id}`} className="text-slate-900 hover:text-indigo-600">{cName(d.companyId)}</Link></td>
                        <td className="px-2 py-2 text-xs text-slate-500 tabular-nums">{val ? val.valuedOn.replace(/-/g, '.') : '평가 없음'}</td>
                        <td className="px-4 py-2 text-right">
                          {fresh ? <span className="text-xs text-emerald-700">연말 평가 있음</span> : <span className="text-xs text-amber-700">4분기 평가 필요</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );
      })}

      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
